import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClientById, updateClient, fetchRequirements } from '../../store/slices/staffingSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import StatusBadge from '../../components/StatusBadge';
import StatusCounts from '../../components/StatusCounts';
import { formatDateTime } from '../../utils/formatDate';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentClient, requirements, loading } = useSelector((state) => state.staffing);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(fetchClientById(id));
      dispatch(fetchRequirements({ client_id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentClient) {
      setFormData({
        name: currentClient.name,
        contact_person: currentClient.contact_person,
        phone: currentClient.phone,
        email: currentClient.email,
        company_details: currentClient.company_details || ''
      });
    }
  }, [currentClient]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      await dispatch(updateClient({ id, data: formData })).unwrap();
      setEditing(false);
    } catch (error) {
      alert('Failed to update client: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !currentClient) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/staffing')}>
          <i className="ti ti-arrow-left me-1" />
          Back to Dashboard
        </button>

        <div className="card mb-3">
          <div className="card-body">
            {editing ? (
              <form onSubmit={handleUpdate}>
                <h2 className="fs-5 mb-3">Edit Client</h2>

                <div className="mb-3">
                  <label className="form-label">Client Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Contact Person *</label>
                  <input
                    type="text"
                    name="contact_person"
                    className="form-control"
                    value={formData.contact_person}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Company Details</label>
                  <textarea
                    name="company_details"
                    className="form-control"
                    rows={4}
                    value={formData.company_details}
                    onChange={handleChange}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <h2 className="fs-4 mb-0">{currentClient.name}</h2>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/staffing/clients/${id}/requirements/new`)}
                    >
                      <i className="ti ti-plus me-1" />
                      Add Requirement
                    </button>
                  </div>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CONTACT PERSON</label>
                    <p className="mb-0">{currentClient.contact_person}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">EMAIL</label>
                    <p className="mb-0">{currentClient.email}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">PHONE</label>
                    <p className="mb-0">{currentClient.phone}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED</label>
                    <p className="mb-0">{formatDateTime(currentClient.created_at)}</p>
                  </div>
                  {currentClient.company_details && (
                    <div className="col-12">
                      <label className="text-muted small fw-semibold">COMPANY DETAILS</label>
                      <p className="mb-0">{currentClient.company_details}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="card table-responsive">
          <div className="card-header bg-white">
            <h3 className="fs-6 mb-0">Requirements</h3>
          </div>
          <table className="table mb-0 text-nowrap table-hover">
            <thead className="table-light border-light">
              <tr>
                <th>Title</th>
                <th>Skills Required</th>
                <th>Experience</th>
                <th>Positions</th>
                <th>Status</th>
                <th>Candidates</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requirements.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    No requirements yet
                  </td>
                </tr>
              ) : (
                requirements.map((req) => (
                  <tr key={req._id} className="align-middle">
                    <td className="fw-medium">{req.title}</td>
                    <td>{req.skills_required?.join(', ')}</td>
                    <td>{req.experience_required}</td>
                    <td>{req.number_of_positions}</td>
                    <td><StatusBadge status={req.status} /></td>
                    <td><StatusCounts stats={req.candidate_stats} emptyText="No candidates" /></td>
                    <td>{formatDateTime(req.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/staffing/requirements/${req._id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default ClientDetail;
